import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

/**
 * InteractiveMessage Component
 * Renders checkbox groups within AI message bubbles for conversational forms
 * Designed to be reusable for future interactive features (CTAs, resource suggestions)
 */
const InteractiveMessage = ({ interactiveData, onSubmit, disabled = false }) => {
  const { stage, checkboxGroup, question } = interactiveData;

  // Local state for checkbox selections
  const [selections, setSelections] = useState([]);
  const [otherText, setOtherText] = useState('');

  // Handle checkbox toggle
  const handleCheckboxToggle = (optionId) => {
    if (disabled) return;

    setSelections(prev => {
      if (prev.includes(optionId)) {
        // Uncheck
        return prev.filter(id => id !== optionId);
      } else {
        // Check
        return [...prev, optionId];
      }
    });
  };

  // Handle submit
  const handleSubmit = () => {
    if (disabled || selections.length === 0) return;

    const submissionData = {
      stage,
      selections,
      otherText: selections.includes('other') ? otherText : null,
      checkboxGroupId: checkboxGroup.id
    };

    onSubmit(submissionData);
  };

  // Check if "other" option is selected
  const otherSelected = selections.includes('other');
  const canSubmit = selections.length > 0;

  return (
    <div className="bg-gray-50 rounded-lg p-4 my-3 border border-gray-200">
      {/* Question */}
      {question && (
        <p className="text-text-primary font-medium mb-4">{question}</p>
      )}

      {/* Checkbox options */}
      <div className="space-y-2 mb-4">
        {checkboxGroup.options.map(option => (
          <div key={option.id} className="flex items-start">
            <button
              type="button"
              onClick={() => handleCheckboxToggle(option.id)}
              disabled={disabled}
              className={`flex items-start w-full text-left p-3 rounded-md transition-all ${
                selections.includes(option.id)
                  ? 'bg-primary-50 border-2 border-primary'
                  : 'bg-white border-2 border-gray-200 hover:border-gray-300'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <FontAwesomeIcon
                icon={selections.includes(option.id) ? faCheckCircle : faCircle}
                className={`mt-0.5 mr-3 flex-shrink-0 ${
                  selections.includes(option.id) ? 'text-primary' : 'text-gray-300'
                }`}
              />
              <div className="flex-1">
                <div className="font-medium text-text-primary">{option.label}</div>
                {option.description && (
                  <div className="text-sm text-text-secondary mt-1">{option.description}</div>
                )}
              </div>
            </button>
          </div>
        ))}
      </div>

      {/* "Annet" text field if "other" is selected */}
      {otherSelected && (
        <div className="mb-4">
          <textarea
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="Beskriv gjerne mer detaljert..."
            disabled={disabled}
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            rows={3}
          />
        </div>
      )}

      {/* Submit button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || disabled}
          className={`flex items-center space-x-2 px-6 py-2.5 rounded-lg font-medium transition-all ${
            canSubmit && !disabled
              ? 'bg-primary text-white hover:bg-primary-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <span>Send svar</span>
          <FontAwesomeIcon icon={faPaperPlane} className="text-sm" />
        </button>
      </div>
    </div>
  );
};

export default InteractiveMessage;
